import { calculateDeltas, filterByStartDate } from '../utils/feedDataUtils';
import { FEED_TYPES } from '../constants/feedDataConstants';

/**
 * Processes standard feed types (Remote, Fingerprint, Keycard)
 * Calculates deltas and filters by start date
 *
 * @param {Object} result - Query result object from API
 * @param {Object} range - Date range with startDate and endDate
 * @returns {Object} Processed feed object {deviceId, value}
 */
const processStandardFeed = (result, range) => {
	return {
		deviceId: result.name,
		value: calculateDeltas(result.values, range.startDate, 0),
	};
};

/**
 * Processes People feed type
 * Note: Has a bug in original code (line 174: query.resulldsf)
 * Using 0 as initial value to fix the bug
 *
 * @param {Object} result - Query result object from API
 * @param {Object} range - Date range with startDate and endDate
 * @returns {Object} Processed feed object {deviceId, value}
 */
const processPeopleFeed = (result, range) => {
	return {
		deviceId: result.name,
		value: calculateDeltas(result.values, range.startDate, 0),
	};
};

/**
 * Processes Current Cash feed type
 * Returns raw values without delta calculation, filtered by start date
 *
 * @param {Object} result - Query result object from API
 * @param {Object} range - Date range with startDate and endDate
 * @returns {Object} Processed feed object {deviceId, value}
 */
const processCurrentCashFeed = (result, range) => {
	return {
		deviceId: result.name,
		value: filterByStartDate(result.values, range.startDate),
	};
};

/**
 * Processes Revenue feed type
 * Calculates deltas from raw values (revenue is derived from current cash)
 *
 * @param {Object} result - Query result object from API
 * @param {Object} range - Date range with startDate and endDate
 * @returns {Object} Processed feed object {deviceId, value}
 */
const processRevenueFeed = (result, range) => {
	return {
		deviceId: result.name,
		value: calculateDeltas(result.values, range.startDate, 0),
	};
};

/**
 * Feed processor configuration mapping
 * Maps feed types to their specific processor functions
 */
const FEED_PROCESSORS = {
	[FEED_TYPES.CURRENT_CASH]: processCurrentCashFeed,
	[FEED_TYPES.REMOTE]: processStandardFeed,
	[FEED_TYPES.PEOPLE]: processPeopleFeed,
	[FEED_TYPES.FINGERPRINT]: processStandardFeed,
	[FEED_TYPES.KEYCARD]: processStandardFeed,
};

/**
 * Main feed processor that routes to specific processors based on feed type
 *
 * @param {Object} query - Query object from API response
 * @param {Object} range - Date range with startDate and endDate
 * @returns {Object} Object containing processed feeds or null
 */
export const processFeedQuery = (query, range) => {
	if (!query.results || query.results.length === 0) {
		return null;
	}

	const result = query.results[0];
	// attr might be an array or string, normalize to string
	const attrType = Array.isArray(result.tags.attr) ? result.tags.attr[0] : result.tags.attr;

	// Special case: Current Cash generates both current and revenue feeds
	if (attrType === FEED_TYPES.CURRENT_CASH) {
		return {
			type: 'current_and_revenue',
			currentFeed: processCurrentCashFeed(result, range),
			revenueFeed: processRevenueFeed(result, range),
		};
	}

	// Standard feed types
	const processor = FEED_PROCESSORS[attrType];
	if (!processor) {
		return null;
	}

	return {
		type: attrType.toLowerCase().replace(/ /g, ''),
		feed: processor(result, range),
	};
};

/**
 * Organizes processed feeds into categorized lists
 *
 * @param {Array} queries - Array of query results from API
 * @param {Object} range - Date range with startDate and endDate
 * @returns {Object} Object containing all feed lists
 */
export const organizeFeedData = (queries, range) => {
	const feedLists = {
		currentFeedList: [],
		revenueFeedList: [],
		remoteFeedList: [],
		peopleFeedList: [],
		fingerPrintFeedList: [],
		keycardFeedList: [],
	};

	queries.forEach((query) => {
		const processed = processFeedQuery(query, range);

		if (!processed) {
			return;
		}

		if (processed.type === 'current_and_revenue') {
			if (processed.currentFeed) {
				feedLists.currentFeedList.push(processed.currentFeed);
			}
			if (processed.revenueFeed) {
				feedLists.revenueFeedList.push(processed.revenueFeed);
			}
		} else {
			// Map attribute type to corresponding list
			const listMapping = {
				remote: 'remoteFeedList',
				people: 'peopleFeedList',
				fingerprint: 'fingerPrintFeedList',
				keycard: 'keycardFeedList',
			};

			const listName = listMapping[processed.type];
			if (listName && processed.feed) {
				feedLists[listName].push(processed.feed);
			}
		}
	});

	return feedLists;
};
